import { useState, useEffect } from "react";
import { api, useAuth } from "../Contexts/AuthContext";
import "../styles/roles.css";

export default function Roles() {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    is_base_role: false,
    permissions: []
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rolesRes, permsRes] = await Promise.all([
        api.get("rbac/roles/"),
        api.get("rbac/permissions/")
      ]);
      setRoles(rolesRes.data);
      setPermissions(permsRes.data);
    } catch (error) {
      console.error("Failed to fetch RBAC data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSave = async () => {
    if (!formData.name) return alert("Role name is required");
    try {
      if (editId) {
        await api.put(`rbac/roles/${editId}/`, formData);
      } else {
        await api.post("rbac/roles/", formData);
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error("Failed to save role", error);
      const data = error.response?.data;
      let msg = "Could not save role.";
      if (data) {
        if (typeof data === 'string') msg = data;
        else if (data.detail) msg = data.detail;
        else if (typeof data === 'object') {
          msg = Object.entries(data).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`).join("\n");
        }
      }
      alert("Error:\n" + msg);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure? This will affect all users assigned to this role.")) return;
    try {
      await api.delete(`rbac/roles/${id}/`);
      fetchData();
    } catch (error) {
      console.error("Failed to delete role", error);
      alert("Error: " + (error.response?.data?.detail || "Could not delete role"));
    }
  };

  const togglePermission = (permId) => {
    const current = [...formData.permissions];
    if (current.includes(permId)) {
      setFormData({ ...formData, permissions: current.filter(id => id !== permId) });
    } else {
      setFormData({ ...formData, permissions: [...current, permId] });
    }
  };

  if (loading) return <div className="roles-container">⌛ Loading Workspace Permissions...</div>;

  return (
    <div className="roles-container">
      <div className="roles-header">
        <div>
          <h1>Role Management</h1>
          <p>Define custom roles and access levels for your organization.</p>
        </div>
        <button className="btn-add-role" onClick={() => {
          setFormData({ name: "", description: "", is_base_role: false, permissions: [] });
          setEditId(null);
          setShowModal(true);
        }}>
          + Create New Role
        </button>
      </div>

      <div className="roles-grid">
        {roles.map(role => (
          <div className={`role-card ${role.is_base_role ? 'base' : 'custom'}`} key={role.id}>
            <div className="card-top">
              <span className="badge">{role.is_base_role ? 'System' : 'Custom'}</span>
              <h3>{role.name}</h3>
            </div>
            <p className="description">{role.description || "No description provided."}</p>
            <div className="perms-summary">
              <strong>{role.permissions?.length || 0} Permissions</strong>
              <div className="perms-tags">
                {role.permissions_details?.slice(0, 3).map(p => (
                  <span key={p.id} className="perm-tag">{p.name}</span>
                ))}
                {role.permissions_details?.length > 3 && <span>+{role.permissions_details.length - 3} more</span>}
              </div>
            </div>
            <div className="card-actions">
              {(user?.is_superuser || user?.is_staff || !role.is_base_role) ? (
                <>
                  <button className="btn-edit" onClick={() => {
                    setFormData({ 
                      name: role.name, 
                      description: role.description, 
                      is_base_role: role.is_base_role,
                      permissions: role.permissions 
                    });
                    setEditId(role.id);
                    setShowModal(true);
                  }}>Edit</button>
                  <button className="btn-delete" onClick={() => handleDelete(role.id)}>Delete</button>
                </>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                  <span className="info-text">System roles are read-only</span>
                  <button className="btn-edit" style={{ background: "#f1f5f9", color: "#1e293b", borderColor: "#e2e8f0" }} onClick={() => {
                    const orgName = user?.organization_details?.name || "";
                    setFormData({ 
                      name: orgName ? `${role.name} - ${orgName}` : `${role.name} (Custom)`, 
                      description: `Customized version of ${role.name}. ${role.description}`, 
                      is_base_role: false,
                      permissions: role.permissions 
                    });
                    setEditId(null); // Create new role
                    setShowModal(true);
                  }}>Use as Template</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>{editId ? "Edit Role" : "Create New Role"}</h2>
            <div className="form-body">
              <div className="form-group">
                <label>Role Name</label>
                <input 
                  value={formData.name} 
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Regional Manager"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea 
                  value={formData.description} 
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  placeholder="What can this role do?"
                />
              </div>

              {user?.is_superuser && (
                <div className="form-group-checkbox">
                  <label className="checkbox-container">
                    <input 
                      type="checkbox" 
                      checked={formData.is_base_role} 
                      onChange={e => setFormData({ ...formData, is_base_role: e.target.checked })} 
                    />
                    <span className="checkbox-label">Is System Base Role? (Global)</span>
                  </label>
                </div>
              )}
              
              <div className="permissions-selector">
                <label>Manage Module Permissions</label>
                <div className="perms-groups">
                  {Object.entries(
                    permissions.reduce((acc, p) => {
                      const mod = p.module_name || "General Settings";
                      if (!acc[mod]) acc[mod] = [];
                      acc[mod].push(p);
                      return acc;
                    }, {})
                  ).map(([moduleLabel, modulePerms]) => (
                    <div key={moduleLabel} className="perm-group-section">
                      <h4 className="module-group-title">{moduleLabel}</h4>
                      <div className="perms-list">
                        {modulePerms.map(perm => (
                          <div 
                            key={perm.id} 
                            className={`perm-item ${formData.permissions.includes(perm.id) ? 'active' : ''}`}
                            onClick={() => togglePermission(perm.id)}
                          >
                            <div className="checkbox">{formData.permissions.includes(perm.id) ? '✓' : ''}</div>
                            <div className="perm-info">
                              <strong>{perm.name}</strong>
                              <span>{perm.codename}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn-save" onClick={handleSave}>Save Role</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
